import { Box, Divider, Theme, Typography } from "@outschool/backpack";
import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ClassDetailsNoSectionsQuery,
  ClassDetailsQuery_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import {
  faBirthdayCake,
  faCalendarAlt,
  faCheck,
  faClock,
  faSignalStream,
  faUsers,
  faVideoSlash,
} from "@outschool/icons";
import { TFunction, Trans, useTranslation } from "@outschool/localization";
import {
  ActivitySlugProps,
  activityPath,
  joinMembershipPath,
  membershipSubscribePath,
} from "@outschool/routes";
import {
  AuthTrigger,
  useLoginFlowContext,
  useSession,
} from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  IconBullet,
  SelfPacedIconBullets,
  useActivityString,
  usePriceDetailsString,
} from "@outschool/ui-components-website";
import { useNavigation } from "@outschool/ui-utils";
import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { useCreditOffer } from "../../../../hooks/useCreditOffer";
import { useSubscriptionPlans } from "../../../subscriptions/hooks/useSubscriptionPlans";
import ChooseATimeOrClassScheduleRequestButton from "../../ctas/ChooseATimeOrClassScheduleRequestButton";
import PriceCreditsLineItem from "../../subcomponents/PriceCreditsLineItem";
import { hasOneOnOneAvailabilities } from "../../utils";
import EstimatedValueLineItem from "./EstimatedValueLineItem";

export interface JoinMembershipDesktopActivityCardProps {
  activity: ClassDetailsQuery_ActivityFragment;
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  isReadOnly?: boolean;
  onClickMoreLikeThis?: () => void;
}

const JoinMembershipDesktopActivityCard = ({
  activity,
  activityWithEnrollableTimes,
  leader,
  isReadOnly,
  onClickMoreLikeThis,
}: JoinMembershipDesktopActivityCardProps) => {
  const navigate = useNavigation();
  const { t } = useTranslation(
    "client\\components\\detailPage\\mainBody\\desktopActivityCards\\JoinMembershipDesktopActivityCard",
  );
  const { trialPlan, creditOfferEligiblePlan } = useSubscriptionPlans();
  const { creditOffer } = useCreditOffer();
  const { ageRangeString, classSizeString } = useActivityString();

  // Prefer credit offer eligible plan over trial plan if user is eligible
  const defaultPlan = creditOffer ? creditOfferEligiblePlan : trialPlan;
  const {
    classDurationShortString,
    weeklyMeetingsSearchString,
    videoMeetingsString,
  } = usePriceDetailsString();

  const callToActionMemo = useMemo(() => {
    const ChooseATimeOrClassScheduleRequestButtonComponent = (
      <ChooseATimeOrClassScheduleRequestButton
        activity={activity}
        leader={leader}
        variant="contained"
        sx={{ width: "100%" }}
        /* eslint-disable-next-line i18next/no-literal-string */
        locationRenderedOn="DesktopHeaderCallout"
      />
    );

    if (isReadOnly) {
      return null;
    }

    // Non-tutoring class that is sold out and doesn't allow waitlist
    if (activity.isSoldOut && !activity.allowWaitlist) {
      return (
        <>
          <Typography
            variant="subtitle1"
            sx={{ color: "error.600", marginBottom: "1em" }}
          >
            {t`This class is sold out`}
          </Typography>
          {ChooseATimeOrClassScheduleRequestButtonComponent}
        </>
      );
    }
    // Class was previously published but delisted, CDP for class still accessible
    if (activity.wasPublishedInThePast) {
      return (
        <>
          <Typography
            variant="subtitle1"
            sx={{ color: "error.600", marginBottom: "1em" }}
          >
            {t`This class is no longer offered`}
          </Typography>
          {onClickMoreLikeThis && (
            <TrackedButton
              variant="contained"
              onClick={onClickMoreLikeThis}
              sx={{ width: "100%" }}
              trackingName="cdp_see_more_like_this"
              size="large"
            >
              {t`See More Like This`}
            </TrackedButton>
          )}
        </>
      );
    }
    if (activity.hasTeacherSchedule) {
      if (leader.hasBackgroundCheckExpired && !leader.ownerOfSellerOrg) {
        return (
          <>
            <Typography
              variant="subtitle1"
              sx={{ color: "error.600", marginBottom: "1em" }}
            >
              {t`This class is temporarily unavailable`}
            </Typography>
            {onClickMoreLikeThis && (
              <TrackedButton
                variant="contained"
                onClick={onClickMoreLikeThis}
                sx={{ width: "100%" }}
                trackingName="cdp_see_more_like_this"
                size="large"
              >
                {t`See More Like This`}
              </TrackedButton>
            )}
          </>
        );
      }
      // Tutoring class that is sold out
      if (
        !!activityWithEnrollableTimes &&
        !hasOneOnOneAvailabilities(activityWithEnrollableTimes)
      ) {
        return (
          <>
            <Typography
              variant="subtitle1"
              sx={{ color: "error.600", marginBottom: "1em" }}
            >
              {t`This class is sold out`}
            </Typography>
            {ChooseATimeOrClassScheduleRequestButtonComponent}
          </>
        );
      }
    }

    // Catch all for other non-enrollable states
    if (
      !activity.canEnroll &&
      !activity.allows_late_enrollments_for_fixed_length_classes
    ) {
      return ChooseATimeOrClassScheduleRequestButtonComponent;
    }

    // Default, class is enrollable
    return <TryForFreeButton activity={activity} t={t} />;
  }, [
    activity,
    leader,
    isReadOnly,
    onClickMoreLikeThis,
    t,
    activityWithEnrollableTimes,
  ]);

  return (
    <>
      <Box
        sx={(theme: Theme) => ({
          flexShrink: 0,
          width: "320px",
          borderRadius: "1em",
          boxShadow: "0px 4px 8px 0px #0000000F, 0px 0px 4px 0px #0000000A",
          [theme.breakpoints.down("lg")]: {
            width: "290px",
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "primary.50",
            borderRadius: "1em 1em 0 0",
            padding: 20,
            paddingTop: 32,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <PriceCreditsLineItem
              activity={activity}
              displayFullPriceOption={false}
            />
            {!activity.isIncludedWithMembership && (
              <EstimatedValueLineItem activity={activity} />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            paddingX: 16,
            paddingY: 20,
          }}
        >
          {activity.isSelfPaced ? (
            <SelfPacedIconBullets activity={activity} />
          ) : (
            <>
              <IconBullet
                icon={faCalendarAlt}
                iconSx={{ color: "grey.600" }}
                text={weeklyMeetingsSearchString(activity)}
              />
              {!activity.isClub && (
                <IconBullet
                  icon={faClock}
                  iconSx={{ color: "grey.600" }}
                  text={classDurationShortString(activity)}
                />
              )}
            </>
          )}
          {activity.isClub && (
            <IconBullet
              icon={faCheck}
              iconSx={{ color: "grey.600" }}
              text={t("{{count}} active learner", {
                count: activity.clubSection?.validEnrollmentCount ?? 0,
                defaultValue_plural: "{{count}} active learners",
              })}
            />
          )}
          {!activity.isSelfPaced && (
            <IconBullet
              iconSx={{ color: "grey.600" }}
              icon={
                !activity.isFlexSchedule && !activity.isClub
                  ? faSignalStream
                  : faVideoSlash
              }
              text={videoMeetingsString(activity)}
            />
          )}
          <IconBullet
            icon={faBirthdayCake}
            iconSx={{ color: "grey.600" }}
            text={ageRangeString(activity)}
          />
          {!activity.isClub &&
            !activity.isFlexSchedule &&
            activity.size_max != 1 && (
              <IconBullet
                icon={faUsers}
                iconSx={{ color: "grey.600" }}
                text={t("{{learnerCount}} learners per class", {
                  learnerCount: classSizeString(activity),
                })}
              />
            )}
        </Box>
        <Divider sx={{ borderColor: "neutral.200" }} />
        <Box
          flex
          sx={{ flexDirection: "column", paddingX: 16, paddingY: 20, gap: 16 }}
        >
          <Box flex sx={{ flexDirection: "column", gap: 8 }}>
            <Typography variant="subtitle1">
              {t("Try your first month free.")}
            </Typography>
            <Trans t={t}>
              <Typography variant="body2">
                Get{" "}
                <span style={{ fontWeight: 500 }}>
                  {{ credits: defaultPlan?.numCredits ?? 80 }} credits
                </span>{" "}
                to explore thousands of live classes and tutoring sessions when
                you join.
              </Typography>
            </Trans>
          </Box>

          {!isReadOnly && callToActionMemo && (
            <Box
              sx={{
                textAlign: "center",
              }}
              data-test-id="card-cta"
            >
              {callToActionMemo}
            </Box>
          )}
        </Box>
      </Box>
      <TrackedButton
        variant="link"
        onClick={() => {
          navigate(joinMembershipPath());
        }}
        sx={{
          width: "100%",
          marginTop: 32,
          "&:hover:not(:disabled)": {
            textDecoration: "none !important",
          },
          "&:focus:not(:disabled)": {
            textDecoration: "none !important",
          },
        }}
        trackingName="cdp_see_more_plans"
      >
        {t("See More Plans")}
      </TrackedButton>
    </>
  );
};

export default JoinMembershipDesktopActivityCard;

const TryForFreeButton = ({
  activity,
  t,
}: {
  activity: ActivitySlugProps;
  t: TFunction;
}) => {
  const { enterLoginFlow } = useLoginFlowContext();
  const location = useLocation();
  const { isLoggedIn } = useSession();
  const navigate = useNavigation();
  const {
    trialPlan,
    creditOfferEligiblePlan,
    loading: subscriptionPlansLoading,
  } = useSubscriptionPlans();
  const { creditOffer } = useCreditOffer();

  // Prefer credit offer eligible plan over trial plan if user is eligible
  const defaultPlan = creditOffer ? creditOfferEligiblePlan : trialPlan;

  const onClickCallback = React.useCallback(
    (event?: any) => {
      if (!defaultPlan) {
        return;
      }
      event?.stopPropagation();
      const basePath = activityPath(activity);
      if (isLoggedIn) {
        navigate(
          membershipSubscribePath(defaultPlan.uid, {
            couponCode: "BTS40",
            enroll_path: encodeURIComponent(basePath),
          }),
        );
      } else {
        const queryParams = new URLSearchParams(location.search);

        queryParams.set("joinMembershipModal", "true");
        queryParams.set("redirectAfterPath", basePath);
        const loginUrl = `${basePath}?${queryParams.toString()}`;

        queryParams.set("collectLearnerData", "true");
        const signUpUrl = `${basePath}?${queryParams.toString()}`;

        enterLoginFlow({
          authTrigger: AuthTrigger.JOIN_OUTSCHOOL,
          newUrlAfterSignUp: signUpUrl,
          newUrlAfterLogin: loginUrl,
          shouldNavigateOverwrite: false,
        });
      }
    },
    [location, enterLoginFlow, activity, isLoggedIn, navigate, defaultPlan],
  );

  return (
    <Box>
      <TrackedButton
        onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          event.stopPropagation();
          onClickCallback(event);
        }}
        trackingName="desktop_activity_card_try_for_free"
        trackingUniqueId={activity.uid}
        sx={{ width: "100%" }}
        size="large"
        variant="contained"
        disabled={subscriptionPlansLoading || !defaultPlan}
      >
        {t("Try for Free")}
      </TrackedButton>
    </Box>
  );
};
